import { ListItem, List, Checkbox, Typography } from '@mui/material';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, IconButton } from '@engyalo/design-system';
import { useTheme } from '@mui/material/styles';
import { Container, ButtonContainer, NotFoundContainer, OptionItem, CheckboxIcon, CustomPopover } from './style';
import { CustomChip } from '../style';
import CreateTagModal from '../CreateTagModal';
import useMenuTags from './useMenuTags';
import InputSearch from '../../InputSearch';
import { Tags } from '../../../services/types';
import { tagsColor } from '../../../helpers/tagsColors';

export interface IMenuTags {
  anchorEl?: HTMLDivElement | HTMLButtonElement;
  handleClose: () => void;
  chatId: string;
  clientToken: string;
  contactId: string;
  hasPermission: boolean;
}

export interface ITagsList {
  tag: Tags;
  selected: boolean;
}

const MenuTags: React.FC<IMenuTags> = (props) => {
  const theme = useTheme();
  const { anchorEl, handleClose, chatId, clientToken, contactId, hasPermission } = props;
  const { t } = useTranslation();

  const {
    selectedTags,
    handleChecked,
    setOpenModal,
    setCurrentTag,
    openModal,
    currentTag,
    filterTags,
    typeDescription,
    handleCloseModal,
    loadTags,
  } = useMenuTags({
    anchorEl,
    clientToken,
    chatId,
    contactId,
  });

  const [open, setOpen] = useState<boolean>(false);
  const [inputText, setInputText] = useState('');
  const isSearching = Boolean(inputText);
  const showNewTagButton = Boolean(hasPermission && isSearching);

  useEffect(() => {
    if (anchorEl) {
      setOpen(Boolean(anchorEl));
    }
  }, [anchorEl]);

  const renderTagsList = () => (
    <Container data-testid="menuTags">
      <InputSearch
        placeholder="searchTags"
        value={inputText}
        onChange={(value) => {
          filterTags(typeDescription, value);
          setInputText(value);
        }}
      />

      {selectedTags.length ? (
        <List
          data-testid="tagList"
          onScroll={(ev) => {
            const isBottom =
              ev.currentTarget.scrollHeight - ev.currentTarget.scrollTop === ev.currentTarget.clientHeight;
            if (isBottom) {
              loadTags();
            }
          }}
          disablePadding
          sx={{
            width: '100%',
            minHeight: '20px',
            maxHeight: '200px',
            overflowY: 'scroll',
          }}
        >
          {selectedTags.map((item, index) => (
            <ListItem
              divider={index !== selectedTags.length - 1}
              disablePadding
              sx={{ height: '44px' }}
              key={item.tag._id}
            >
              <OptionItem>
                <div>
                  <Checkbox
                    data-testid="tagItem"
                    disableRipple
                    disableTouchRipple
                    checked={item.selected}
                    sx={{
                      padding: 0,
                      width: '16px',
                      heigth: '16px',
                      marginRight: '16px',
                      borderRadius: '4px',
                      '& .MuiSvgIcon-root': {
                        fontSize: '21px',
                        padding: 0,
                      },
                      '& input:focus + *': {
                        background: theme.palette.informational.light,
                        borderRadius: '4px',
                      },
                    }}
                    icon={<CheckboxIcon />}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      handleChecked(event.target.checked, item.tag.name, typeDescription)
                    }
                  />
                  <CustomChip
                    size="small"
                    label={item.tag.name}
                    customcolor={tagsColor(item.tag.name)}
                    data-testid="tagLabel"
                  />
                </div>
                {hasPermission && (
                  <IconButton
                    size="small"
                    color="neutral"
                    onClick={(event) => {
                      event.stopPropagation();
                      setOpenModal((oldState) => !oldState);
                      setCurrentTag(item.tag);
                    }}
                  >
                    <i className="fa-regular fa-pen" />
                  </IconButton>
                )}
              </OptionItem>
            </ListItem>
          ))}
        </List>
      ) : (
        <NotFoundContainer>
          <Typography variant="caption1" color="neutral.var50">
            {t('tagsNotFound')}
          </Typography>
        </NotFoundContainer>
      )}
      {showNewTagButton && (
        <ButtonContainer>
          <Button
            data-testid="newTagButton"
            id="newTag"
            onClick={() => {
              setOpenModal((oldState) => !oldState);
            }}
            fullWidth
            color="neutral2"
            disableElevation
            startIcon={<i className="fa-regular fa-plus" />}
          >
            {`${t('newTag')} `}
            <Typography component="span" variant="body2" color="primary" marginLeft={0.5} textTransform="none">
              {inputText}
            </Typography>
          </Button>
        </ButtonContainer>
      )}

      {openModal && (
        <CreateTagModal
          typeDescription={typeDescription}
          tag={currentTag}
          open={openModal}
          newTagValue={inputText}
          handleCloseModal={() => handleCloseModal()}
        />
      )}
    </Container>
  );

  return (
    <CustomPopover
      open={open}
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      onClose={handleClose}
    >
      <Typography variant="caption2" align="center" component="h3" color="primary">
        {t('chatTags')}
      </Typography>
      {renderTagsList()}
    </CustomPopover>
  );
};

export default MenuTags;
